import { useEffect, useMemo, useState } from "react";

const DAY_MS = 24 * 60 * 60 * 1000;
const TIMELINE_DAY_CELL_PX = 20;
const TIMELINE_MIN_CANVAS_PX = 1120;

const WORKORDER_PHASE_META = {
  concept: { label: "Concept", barClass: "bg-slate-400", dotClass: "bg-slate-500", badgeClass: "bg-slate-100 text-slate-700" },
  gereed_voor_planning: { label: "Voorbereiding", barClass: "bg-amber-400", dotClass: "bg-amber-500", badgeClass: "bg-amber-50 text-amber-700" },
  gepland: { label: "Gepland", barClass: "bg-sky-500", dotClass: "bg-sky-600", badgeClass: "bg-sky-50 text-sky-700" },
  in_uitvoering: { label: "In uitvoering", barClass: "bg-indigo-500", dotClass: "bg-indigo-600", badgeClass: "bg-indigo-50 text-indigo-700" },
  tijdelijk_stilgelegd: { label: "On hold", barClass: "bg-violet-500", dotClass: "bg-violet-600", badgeClass: "bg-violet-50 text-violet-700" },
  afgerond: { label: "Afgerond", barClass: "bg-emerald-500", dotClass: "bg-emerald-600", badgeClass: "bg-emerald-50 text-emerald-700" },
  gesloten: { label: "Gesloten", barClass: "bg-slate-700", dotClass: "bg-slate-800", badgeClass: "bg-slate-200 text-slate-800" },
};

const PROJECT_STATUS_META = {
  nieuw: { label: "Nieuw", barClass: "bg-cyan-500", badgeClass: "bg-cyan-50 text-cyan-700" },
  actief: { label: "Actief", barClass: "bg-blue-600", badgeClass: "bg-blue-50 text-blue-700" },
  on_hold: { label: "On hold", barClass: "bg-violet-600", badgeClass: "bg-violet-50 text-violet-700" },
  afgerond: { label: "Afgerond", barClass: "bg-emerald-600", badgeClass: "bg-emerald-50 text-emerald-700" },
  gearchiveerd: { label: "Gearchiveerd", barClass: "bg-slate-600", badgeClass: "bg-slate-200 text-slate-700" },
};

const WORKORDER_PHASE_ORDER = Object.keys(WORKORDER_PHASE_META);

const FALLBACK_PROJECTS = [
  {
    _id: "demo-project-1",
    projectNummer: "P-2401",
    naam: "Nieuwbouw De Kade",
    startDate: "2026-04-01",
    status: "actief",
    clientName: "Van Dijk Bouw",
    targets: {
      targetStartDate: "2026-04-01",
      targetEndDate: "2026-04-24",
      targetHours: 180,
      targetRevenueExcl: 42000,
    },
  },
  {
    _id: "demo-project-2",
    projectNummer: "P-2402",
    naam: "Renovatie Orion",
    startDate: "2026-04-05",
    status: "on_hold",
    clientName: "Orion Vastgoed",
    targets: {
      targetStartDate: "2026-04-05",
      targetEndDate: "2026-04-28",
      targetHours: 110,
      targetRevenueExcl: 27500,
    },
  },
];

const FALLBACK_ITEMS = [
  { id: "demo-wb-1", projectIdRaw: "demo-project-1", werkbonNummer: "WB-2401", title: "Fundering uitzetten", statusKey: "concept", plannedDate: "2026-04-10", clientName: "Van Dijk Bouw", locationLabel: "Kade 7" },
  { id: "demo-wb-2", projectIdRaw: "demo-project-1", werkbonNummer: "WB-2402", title: "Bekisting plaatsen", statusKey: "gereed_voor_planning", plannedDate: "2026-04-13", clientName: "Van Dijk Bouw", locationLabel: "Kade 7" },
  { id: "demo-wb-3", projectIdRaw: "demo-project-1", werkbonNummer: "WB-2403", title: "Wapening controleren", statusKey: "in_uitvoering", plannedDate: "2026-04-17", clientName: "Van Dijk Bouw", locationLabel: "Kade 7" },
  { id: "demo-wb-4", projectIdRaw: "demo-project-2", werkbonNummer: "WB-2404", title: "Dakrand afwerken", statusKey: "gepland", plannedDate: "2026-04-12", clientName: "Orion Vastgoed", locationLabel: "Orionlaan 12" },
  { id: "demo-wb-5", projectIdRaw: "demo-project-2", werkbonNummer: "WB-2405", title: "Eindcontrole", statusKey: "afgerond", plannedDate: "2026-04-22", clientName: "Orion Vastgoed", locationLabel: "Orionlaan 12" },
];

const FALLBACK_TASKS = [
  { _id: "demo-task-1", projectId: "demo-project-1", title: "Steigerkeuring", prio: "hoog", plannedDate: "2026-04-14", status: "open" },
  { _id: "demo-task-2", projectId: "demo-project-1", title: "Bestelling ankers", prio: "normaal", plannedDate: "2026-04-16", status: "in_uitvoering" },
  { _id: "demo-task-3", projectId: "demo-project-2", title: "Afstemming opdrachtgever", prio: "laag", plannedDate: "2026-04-18", status: "open" },
];

const TIMELINE_COLORS_STORAGE_KEY = "pl.projectTimelineColors.v1";
const DEFAULT_TIMELINE_COLORS = {
  projectBar: "#2563eb",
  weekdayBackground: "#f8fafc",
  gridLine: "#94a3b8",
  weekend: "#94a3b8",
  currentWeek: "#fcd34d",
  todayLine: "#e11d48",
};

function safeDate(value) {
  if (!value) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  parsed.setHours(12, 0, 0, 0);
  return parsed;
}

function toDateInputValue(value) {
  const parsed = safeDate(value);
  if (!parsed) return "";
  const y = parsed.getFullYear();
  const m = String(parsed.getMonth() + 1).padStart(2, "0");
  const d = String(parsed.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function parseNumberInput(value) {
  if (value === "" || value === null || value === undefined) return 0;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function startOfWeek(date) {
  const d = new Date(date);
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  d.setHours(0, 0, 0, 0);
  return d;
}

function endOfWeek(date) {
  const d = startOfWeek(date);
  d.setDate(d.getDate() + 6);
  d.setHours(23, 59, 59, 999);
  return d;
}

function startOfYearWeek(date) {
  return startOfWeek(new Date(date.getFullYear(), 0, 1));
}

function endOfYearWeek(date) {
  return endOfWeek(new Date(date.getFullYear(), 11, 31));
}

function diffDays(startDate, endDate) {
  return Math.max(0, Math.round((endDate.getTime() - startDate.getTime()) / DAY_MS));
}

function signedDiffDays(fromDate, toDate) {
  return Math.round((toDate.getTime() - fromDate.getTime()) / DAY_MS);
}

function addDays(date, days) {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

function clamp(value, min, max) {
  return Math.max(min, Math.min(max, value));
}

function normalizeHexColor(value, fallback) {
  const hex = String(value || "").trim();
  if (/^#[0-9a-fA-F]{6}$/.test(hex)) return hex.toLowerCase();
  return fallback;
}

function withAlpha(hex, alpha) {
  const clean = normalizeHexColor(hex, "#000000").slice(1);
  const r = Number.parseInt(clean.slice(0, 2), 16);
  const g = Number.parseInt(clean.slice(2, 4), 16);
  const b = Number.parseInt(clean.slice(4, 6), 16);
  const a = clamp(Number(alpha), 0, 1);
  return `rgba(${r}, ${g}, ${b}, ${a})`;
}

function parseTimelineColors(raw) {
  const source = raw && typeof raw === "object" ? raw : {};
  return {
    projectBar: normalizeHexColor(source.projectBar, DEFAULT_TIMELINE_COLORS.projectBar),
    weekdayBackground: normalizeHexColor(source.weekdayBackground, DEFAULT_TIMELINE_COLORS.weekdayBackground),
    gridLine: normalizeHexColor(source.gridLine, DEFAULT_TIMELINE_COLORS.gridLine),
    weekend: normalizeHexColor(source.weekend, DEFAULT_TIMELINE_COLORS.weekend),
    currentWeek: normalizeHexColor(source.currentWeek, DEFAULT_TIMELINE_COLORS.currentWeek),
    todayLine: normalizeHexColor(source.todayLine, DEFAULT_TIMELINE_COLORS.todayLine),
  };
}

function normalizeWorkorderStatus(value) {
  const key = String(value || "concept").trim().toLowerCase();
  return WORKORDER_PHASE_META[key] ? key : "concept";
}

function normalizeProjectStatus(value) {
  const key = String(value || "actief").trim().toLowerCase();
  return PROJECT_STATUS_META[key] ? key : "actief";
}

function normalizeTaskPrio(value) {
  const key = String(value || "normaal").trim().toLowerCase();
  if (["laag", "normaal", "hoog"].includes(key)) return key;
  return "normaal";
}

function toProjectLabel(project) {
  const nummer = String(project?.projectNummer || project?.number || "").trim();
  const naam = String(project?.naam || project?.title || "").trim();
  if (nummer && naam) return `${nummer} • ${naam}`;
  return naam || nummer || "Zonder projectnaam";
}

function formatDate(value) {
  return new Intl.DateTimeFormat("nl-NL", { day: "2-digit", month: "short" }).format(value);
}

function formatDateLong(value) {
  return new Intl.DateTimeFormat("nl-NL", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  }).format(value);
}

function getIsoWeekNumber(value) {
  const date = new Date(value);
  date.setHours(0, 0, 0, 0);
  date.setDate(date.getDate() + 3 - ((date.getDay() + 6) % 7));
  const week1 = new Date(date.getFullYear(), 0, 4);
  return 1 + Math.round(((date.getTime() - week1.getTime()) / DAY_MS - 3 + ((week1.getDay() + 6) % 7)) / 7);
}

function formatDateWithWeek(value) {
  return `${formatDateLong(value)} (week ${getIsoWeekNumber(value)})`;
}

function formatWeekdayNameForInput(value) {
  const parsed = safeDate(value);
  if (!parsed) return "";
  return new Intl.DateTimeFormat("nl-NL", { weekday: "long" }).format(parsed);
}

function formatDateDMY(value) {
  const parsed = safeDate(value);
  if (!parsed) return "-";
  return new Intl.DateTimeFormat("nl-NL", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(parsed);
}

function formatWeekTickLabel(value) {
  const week = String(getIsoWeekNumber(value)).padStart(2, "0");
  return `W${week} • ${formatDate(value)}`;
}

function formatWeekdayInitial(value) {
  const day = value.getDay();
  if (day === 1) return "ma";
  if (day === 2) return "di";
  if (day === 3) return "woe";
  if (day === 4) return "do";
  if (day === 5) return "vr";
  if (day === 6) return "zat";
  return "zon";
}

function formatRange(start, end) {
  const formatter = new Intl.DateTimeFormat("nl-NL", { day: "2-digit", month: "short", year: "numeric" });
  return `${formatter.format(start)} - ${formatter.format(end)}`;
}

function formatMoney(value) {
  return new Intl.NumberFormat("nl-NL", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(Number(value || 0));
}

function normalizeProjectRecord(raw, index, fallbackDate) {
  const projectId = String(raw?._id || raw?.id || `project-${index + 1}`).trim();
  const startDate = safeDate(raw?.startDate) || addDays(fallbackDate, index);
  const statusKey = normalizeProjectStatus(raw?.status);
  const clientName = String(raw?.clientId?.naam || raw?.clientName || "Onbekende klant").trim() || "Onbekende klant";
  const rawTargets = raw?.targets || {};
  const phaseTargets = Array.isArray(raw?.phaseTargets) ? raw.phaseTargets : [];

  return {
    projectId,
    projectKey: projectId.toLowerCase(),
    projectLabel: toProjectLabel(raw),
    projectNummer: String(raw?.projectNummer || raw?.number || "").trim(),
    clientName,
    startDate,
    statusKey,
    statusMeta: PROJECT_STATUS_META[statusKey],
    targets: {
      targetStartDate: safeDate(rawTargets?.targetStartDate),
      targetEndDate: safeDate(rawTargets?.targetEndDate),
      targetHours: Number(rawTargets?.targetHours || 0),
      targetRevenueExcl: Number(rawTargets?.targetRevenueExcl || 0),
      targetPlannedHours: Number(rawTargets?.targetPlannedHours || 0),
      targetCompletionRate: Number(rawTargets?.targetCompletionRate || 0),
    },
    phaseTargets: phaseTargets
      .map((entry) => ({
        key: String(entry?.key || "").trim().toLowerCase(),
        label: String(entry?.label || "").trim(),
        targetStartDate: safeDate(entry?.targetStartDate),
        targetEndDate: safeDate(entry?.targetEndDate),
        targetCount: Number(entry?.targetCount || 0),
      }))
      .filter((entry) => entry.key),
  };
}

function collectProjectKeyCandidates(project) {
  const values = [project.projectId, project.projectKey, project.projectNummer];
  return Array.from(new Set(values.map((value) => String(value || "").trim().toLowerCase()).filter(Boolean)));
}

function toWorkorderDate(item, projectStartDate) {
  return safeDate(item?.plannedDate) || safeDate(item?.createdAt) || projectStartDate;
}

function buildTimelineModel({ projects, items, tasks }) {
  const now = new Date();
  const hasRealProjects = Array.isArray(projects) && projects.length > 0;
  const hasRealItems = Array.isArray(items) && items.length > 0;
  const hasRealTasks = Array.isArray(tasks) && tasks.length > 0;

  const sourceProjects = hasRealProjects ? projects : FALLBACK_PROJECTS;
  const sourceItems = hasRealItems ? items : FALLBACK_ITEMS;
  const sourceTasks = hasRealTasks ? tasks : FALLBACK_TASKS;

  const normalizedProjects = sourceProjects.map((project, index) => normalizeProjectRecord(project, index, now));

  const itemBuckets = new Map();
  sourceItems.forEach((rawItem, index) => {
    const normalized = {
      id: String(rawItem?.id || rawItem?._id || `wb-${index + 1}`),
      projectIdRaw: String(rawItem?.projectIdRaw || rawItem?.projectId?._id || rawItem?.projectId || "").trim().toLowerCase(),
      projectKey: String(rawItem?.projectKey || "").trim().toLowerCase(),
      werkbonNummer: String(rawItem?.werkbonNummer || rawItem?.nummer || "Werkbon").trim(),
      title: String(rawItem?.title || rawItem?.omschrijving || "Werkbon").trim(),
      statusKey: normalizeWorkorderStatus(rawItem?.statusKey || rawItem?.status),
      plannedDate: rawItem?.plannedDate || null,
      createdAt: rawItem?.createdAt || null,
      clientName: String(rawItem?.clientName || "Onbekende klant").trim() || "Onbekende klant",
      locationLabel: String(rawItem?.locationLabel || "Locatie onbekend").trim() || "Locatie onbekend",
    };

    const keys = Array.from(new Set([normalized.projectIdRaw, normalized.projectKey].filter(Boolean)));
    keys.forEach((key) => {
      if (!itemBuckets.has(key)) itemBuckets.set(key, []);
      itemBuckets.get(key).push(normalized);
    });
  });

  const taskBuckets = new Map();
  sourceTasks.forEach((rawTask, index) => {
    const projectId = String(rawTask?.projectId?._id || rawTask?.projectId || "").trim().toLowerCase();
    if (!projectId) return;
    const normalizedTask = {
      id: String(rawTask?._id || rawTask?.id || `task-${index + 1}`),
      title: String(rawTask?.title || "Taak").trim(),
      prio: normalizeTaskPrio(rawTask?.prio),
      status: String(rawTask?.status || "open").trim().toLowerCase(),
      plannedDate: safeDate(rawTask?.plannedDate) || safeDate(rawTask?.createdAt) || now,
      persisted: /^[0-9a-fA-F]{24}$/.test(String(rawTask?._id || "").trim()),
    };

    if (!taskBuckets.has(projectId)) taskBuckets.set(projectId, []);
    taskBuckets.get(projectId).push(normalizedTask);
  });

  const projectsWithPhases = normalizedProjects.map((project, index) => {
    const keyCandidates = collectProjectKeyCandidates(project);
    const linkedById = new Map();
    keyCandidates.forEach((key) => {
      const bucket = itemBuckets.get(key);
      if (!Array.isArray(bucket)) return;
      bucket.forEach((item) => linkedById.set(item.id, item));
    });

    let linkedItems = Array.from(linkedById.values());
    const projectTasks = keyCandidates
      .flatMap((key) => taskBuckets.get(key) || [])
      .sort((a, b) => a.plannedDate - b.plannedDate);

    if (!linkedItems.length) {
      linkedItems = sourceItems
        .filter((item) => {
          const label = String(item?.projectLabel || item?.projectNaam || "").trim().toLowerCase();
          return label && label === project.projectLabel.trim().toLowerCase();
        })
        .map((item, itemIndex) => ({
          id: String(item?.id || item?._id || `wb-fallback-${index}-${itemIndex}`),
          projectIdRaw: String(item?.projectIdRaw || "").trim().toLowerCase(),
          projectKey: String(item?.projectKey || "").trim().toLowerCase(),
          werkbonNummer: String(item?.werkbonNummer || item?.nummer || "Werkbon"),
          title: String(item?.title || item?.omschrijving || "Werkbon"),
          statusKey: normalizeWorkorderStatus(item?.statusKey || item?.status),
          plannedDate: item?.plannedDate || null,
          createdAt: item?.createdAt || null,
          clientName: String(item?.clientName || project.clientName || "Onbekende klant"),
          locationLabel: String(item?.locationLabel || "Locatie onbekend"),
        }));
    }

    const phaseTargetMap = new Map(project.phaseTargets.map((target) => [target.key, target]));

    const phases = WORKORDER_PHASE_ORDER.map((phaseKey) => {
      const phaseItems = linkedItems
        .filter((item) => item.statusKey === phaseKey)
        .map((item) => ({ ...item, timelineDate: toWorkorderDate(item, project.startDate) }))
        .sort((a, b) => a.timelineDate - b.timelineDate);

      const target = phaseTargetMap.get(phaseKey) || null;

      if (!phaseItems.length && !target) return null;

      const startDate = phaseItems.length
        ? phaseItems[0].timelineDate
        : target?.targetStartDate || project.startDate;
      const endDate = phaseItems.length
        ? phaseItems[phaseItems.length - 1].timelineDate
        : target?.targetEndDate || startDate;
      const phaseMeta = WORKORDER_PHASE_META[phaseKey];

      return {
        key: phaseKey,
        label: phaseMeta.label,
        barClass: phaseMeta.barClass,
        dotClass: phaseMeta.dotClass,
        badgeClass: phaseMeta.badgeClass,
        count: phaseItems.length,
        startDate,
        endDate,
        items: phaseItems,
        target,
      };
    }).filter(Boolean);

    const earliestPhaseStart = phases.length
      ? phases.reduce((min, phase) => (phase.startDate < min ? phase.startDate : min), phases[0].startDate)
      : project.startDate;
    const latestPhaseEnd = phases.length
      ? phases.reduce((max, phase) => (phase.endDate > max ? phase.endDate : max), phases[0].endDate)
      : addDays(project.startDate, 7);

    const timelineStart = project.startDate || earliestPhaseStart || addDays(now, index);
    const timelineEnd = latestPhaseEnd || addDays(timelineStart, 7);

    return {
      ...project,
      items: linkedItems,
      tasks: projectTasks,
      phases,
      timelineStart,
      timelineEnd,
      durationDays: diffDays(timelineStart, timelineEnd) + 1,
    };
  });

  const projectsSorted = projectsWithPhases.sort((a, b) => a.timelineStart - b.timelineStart);

  const dataMinDate = projectsSorted.length
    ? startOfWeek(projectsSorted.reduce((min, project) => (project.timelineStart < min ? project.timelineStart : min), projectsSorted[0].timelineStart))
    : startOfWeek(now);

  const dataMaxDate = projectsSorted.length
    ? endOfWeek(projectsSorted.reduce((max, project) => (project.timelineEnd > max ? project.timelineEnd : max), projectsSorted[0].timelineEnd))
    : endOfWeek(now);

  const yearMinDate = startOfYearWeek(now);
  const yearMaxDate = endOfYearWeek(now);

  const minDate = dataMinDate < yearMinDate ? dataMinDate : yearMinDate;
  const maxDate = dataMaxDate > yearMaxDate ? dataMaxDate : yearMaxDate;

  const totalDays = Math.max(7, diffDays(minDate, maxDate) + 1);
  const totalItems = projectsSorted.reduce((sum, project) => sum + project.items.length, 0);
  const totalTasks = projectsSorted.reduce((sum, project) => sum + project.tasks.length, 0);
  const timelineCanvasWidth = Math.max(
    TIMELINE_MIN_CANVAS_PX,
    Math.round(totalDays * TIMELINE_DAY_CELL_PX)
  );

  const weekTicks = [];
  for (let offset = 0; offset < totalDays; offset += 7) {
    weekTicks.push(addDays(minDate, offset));
  }

  return {
    projects: projectsSorted,
    minDate,
    maxDate,
    totalDays,
    totalItems,
    totalTasks,
    timelineCanvasWidth,
    weekTicks,
    usingFallback: !hasRealProjects && !hasRealItems && !hasRealTasks,
  };
}

function buildTargetDraft(project) {
  const phaseTargetMap = new Map((project?.phaseTargets || []).map((entry) => [entry.key, entry]));

  return {
    targetStartDate: toDateInputValue(project?.targets?.targetStartDate),
    targetEndDate: toDateInputValue(project?.targets?.targetEndDate),
    targetHours: String(Number(project?.targets?.targetHours || 0)),
    targetRevenueExcl: String(Number(project?.targets?.targetRevenueExcl || 0)),
    targetPlannedHours: String(Number(project?.targets?.targetPlannedHours || 0)),
    targetCompletionRate: String(Number(project?.targets?.targetCompletionRate || 0)),
    phaseTargets: WORKORDER_PHASE_ORDER.map((key) => ({
      key,
      label: WORKORDER_PHASE_META[key].label,
      targetStartDate: toDateInputValue(phaseTargetMap.get(key)?.targetStartDate),
      targetEndDate: toDateInputValue(phaseTargetMap.get(key)?.targetEndDate),
      targetCount: String(Number(phaseTargetMap.get(key)?.targetCount || 0)),
    })),
  };
}

export default function ProjectTimelineDemo({
  projects = [],
  items = [],
  tasks = [],
  savingProjectId = "",
  creatingTaskProjectId = "",
  replanningTaskId = "",
  updatingTaskId = "",
  deletingTaskId = "",
  onSaveTargets,
  onCreateTask,
  onReplanTask,
  onUpdateTask,
  onDeleteTask,
  onProjectOpen,
  onWorkorderOpen,
}) {
  const [timelineColors, setTimelineColors] = useState(DEFAULT_TIMELINE_COLORS);
  const [showColorSettings, setShowColorSettings] = useState(false);
  const [selectedProjectKey, setSelectedProjectKey] = useState("");
  const [selectedPhaseKey, setSelectedPhaseKey] = useState("");
  const [targetDraft, setTargetDraft] = useState(() => buildTargetDraft(null));
  const [taskDraft, setTaskDraft] = useState({
    title: "",
    plannedDate: "",
    prio: "normaal",
  });
  const [quickTaskDraft, setQuickTaskDraft] = useState(null);
  const [selectedTaskId, setSelectedTaskId] = useState("");
  const [taskEditDraft, setTaskEditDraft] = useState({
    title: "",
    plannedDate: "",
    prio: "normaal",
    status: "open",
  });

  const model = useMemo(() => buildTimelineModel({ projects, items, tasks }), [projects, items, tasks]);
  const today = safeDate(new Date());
  const todayInRange =
    today instanceof Date &&
    !Number.isNaN(today.getTime()) &&
    today.getTime() >= model.minDate.getTime() &&
    today.getTime() <= model.maxDate.getTime();
  const currentWeekStart = today ? startOfWeek(today) : null;
  const currentWeekEnd = today ? endOfWeek(today) : null;
  const weekInRange =
    currentWeekStart instanceof Date &&
    currentWeekEnd instanceof Date &&
    currentWeekEnd.getTime() >= model.minDate.getTime() &&
    currentWeekStart.getTime() <= model.maxDate.getTime();
  const weekStartDate = weekInRange
    ? new Date(Math.max(currentWeekStart.getTime(), model.minDate.getTime()))
    : null;
  const weekEndDate = weekInRange
    ? new Date(Math.min(currentWeekEnd.getTime(), model.maxDate.getTime()))
    : null;
  const weekLeftPercent =
    weekStartDate && model.totalDays > 0
      ? (clamp(diffDays(model.minDate, weekStartDate), 0, model.totalDays) / model.totalDays) * 100
      : 0;
  const weekWidthPercent =
    weekStartDate && weekEndDate && model.totalDays > 0
      ? Math.max(
          1.5,
          ((diffDays(weekStartDate, weekEndDate) + 1) / model.totalDays) * 100
        )
      : 0;
  const todayLeftPercent =
    todayInRange && model.totalDays > 0
      ? (clamp(diffDays(model.minDate, today), 0, model.totalDays) / model.totalDays) * 100
      : 0;
  const weekendBands = useMemo(() => {
    if (model.totalDays <= 0) return [];
    const bands = [];
    for (let offset = 0; offset < model.totalDays; offset += 1) {
      const dayDate = addDays(model.minDate, offset);
      const day = dayDate.getDay();
      if (day !== 6 && day !== 0) continue;
      const left = (clamp(offset, 0, model.totalDays) / model.totalDays) * 100;
      const width = (1 / model.totalDays) * 100;
      bands.push({
        key: `${dayDate.toISOString()}-${day}`,
        left,
        width,
        label: day === 6 ? "Zaterdag" : "Zondag",
      });
    }
    return bands;
  }, [model.minDate, model.totalDays]);
  const dayTicks = useMemo(() => {
    const ticks = [];
    for (let offset = 0; offset < model.totalDays; offset += 1) {
      ticks.push(addDays(model.minDate, offset));
    }
    return ticks;
  }, [model.minDate, model.totalDays]);
  const gridLineColorSoft = useMemo(() => withAlpha(timelineColors.gridLine, 0.18), [timelineColors.gridLine]);
  const weekendColorSoft = useMemo(() => withAlpha(timelineColors.weekend, 0.35), [timelineColors.weekend]);
  const currentWeekColorSoft = useMemo(() => withAlpha(timelineColors.currentWeek, 0.45), [timelineColors.currentWeek]);
  const weekendHeaderColorSoft = useMemo(() => withAlpha(timelineColors.weekend, 0.12), [timelineColors.weekend]);
  const dayCellWidthPercent = useMemo(
    () => (model.totalDays > 0 ? 100 / model.totalDays : 0),
    [model.totalDays]
  );

  useEffect(() => {
    try {
      const raw = window.localStorage.getItem(TIMELINE_COLORS_STORAGE_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      setTimelineColors(parseTimelineColors(parsed));
    } catch {
      setTimelineColors(DEFAULT_TIMELINE_COLORS);
    }
  }, []);

  useEffect(() => {
    try {
      window.localStorage.setItem(TIMELINE_COLORS_STORAGE_KEY, JSON.stringify(timelineColors));
    } catch {
      // opslag kan geblokkeerd zijn (private mode), geen harde fout tonen
    }
  }, [timelineColors]);

  const selectedProject = model.projects.find((project) => project.projectKey === selectedProjectKey) || null;
  const selectedPhase = selectedProject?.phases.find((phase) => phase.key === selectedPhaseKey) || null;
  const selectedTask = selectedProject?.tasks.find((task) => String(task.id || "") === String(selectedTaskId || "")) || null;

  useEffect(() => {
    setTargetDraft(buildTargetDraft(selectedProject));
  }, [selectedProjectKey, selectedProject]);

  useEffect(() => {
    if (!selectedProject) {
      setTaskDraft({ title: "", plannedDate: "", prio: "normaal" });
      setSelectedTaskId("");
      return;
    }
    setTaskDraft({
      title: "",
      plannedDate: toDateInputValue(selectedProject.timelineStart),
      prio: "normaal",
    });
  }, [selectedProjectKey, selectedProject]);

  useEffect(() => {
    if (!selectedTask) {
      setTaskEditDraft({ title: "", plannedDate: "", prio: "normaal", status: "open" });
      return;
    }
    setTaskEditDraft({
      title: String(selectedTask.title || ""),
      plannedDate: toDateInputValue(selectedTask.plannedDate),
      prio: selectedTask.prio || "normaal",
      status: selectedTask.status || "open",
    });
  }, [selectedTask]);

  const targetDeltaDays = useMemo(() => {
    if (!selectedProject?.targets?.targetEndDate) return null;
    return signedDiffDays(selectedProject.targets.targetEndDate, selectedProject.timelineEnd);
  }, [selectedProject]);

  const updateTimelineColor = (key, value) => {
    setTimelineColors((prev) => ({
      ...prev,
      [key]: normalizeHexColor(value, DEFAULT_TIMELINE_COLORS[key]),
    }));
  };

  const resetTimelineColors = () => {
    setTimelineColors(DEFAULT_TIMELINE_COLORS);
  };

  const isSavingCurrent = selectedProject && String(savingProjectId || "") === String(selectedProject.projectId || "");
  const isCreatingTaskCurrent =
    selectedProject && String(creatingTaskProjectId || "") === String(selectedProject.projectId || "");

  const submitTargets = async (event) => {
    event.preventDefault();
    if (!selectedProject?.projectId || !onSaveTargets) return;

    await onSaveTargets(selectedProject.projectId, {
      targets: {
        targetStartDate: targetDraft.targetStartDate || null,
        targetEndDate: targetDraft.targetEndDate || null,
        targetHours: parseNumberInput(targetDraft.targetHours),
        targetRevenueExcl: parseNumberInput(targetDraft.targetRevenueExcl),
        targetPlannedHours: parseNumberInput(targetDraft.targetPlannedHours),
        targetCompletionRate: parseNumberInput(targetDraft.targetCompletionRate),
      },
      phaseTargets: targetDraft.phaseTargets.map((phaseTarget) => ({
        key: phaseTarget.key,
        label: phaseTarget.label,
        targetStartDate: phaseTarget.targetStartDate || null,
        targetEndDate: phaseTarget.targetEndDate || null,
        targetCount: parseNumberInput(phaseTarget.targetCount),
      })),
    });
  };

  const submitTask = async (event) => {
    event.preventDefault();
    if (!selectedProject?.projectId || !onCreateTask) return;
    if (!String(taskDraft.title || "").trim()) return;

    await onCreateTask(selectedProject.projectId, {
      title: String(taskDraft.title || "").trim(),
      plannedDate: taskDraft.plannedDate || null,
      prio: taskDraft.prio || "normaal",
    });

    setTaskDraft((prev) => ({ ...prev, title: "" }));
  };

  const submitQuickTask = async (project) => {
    if (!project?.projectId || !onCreateTask || !quickTaskDraft) return;
    if (!String(quickTaskDraft.title || "").trim()) return;

    await onCreateTask(project.projectId, {
      title: String(quickTaskDraft.title || "").trim(),
      plannedDate: quickTaskDraft.plannedDate || null,
      prio: quickTaskDraft.prio || "normaal",
    });

    setQuickTaskDraft(null);
  };

  const handleTaskDragStart = (event, task, projectKey) => {
    if (!task?.persisted) return;
    setSelectedProjectKey(String(projectKey || ""));
    setSelectedTaskId(String(task.id || ""));
    event.dataTransfer.effectAllowed = "move";
    event.dataTransfer.setData("application/x-logboek24-task-id", String(task.id || ""));
  };

  const handleTaskDrop = async (event, project) => {
    if (!onReplanTask) return;
    const taskId = String(event.dataTransfer.getData("application/x-logboek24-task-id") || "").trim();
    if (!taskId) return;

    event.preventDefault();
    const rect = event.currentTarget.getBoundingClientRect();
    const rawPercent = ((event.clientX - rect.left) / rect.width) * 100;
    const leftPercent = clamp(rawPercent, 0, 100);
    const maxOffset = Math.max(0, model.totalDays - 1);
    const dayOffset = Math.round((leftPercent / 100) * maxOffset);
    const nextDate = addDays(model.minDate, dayOffset);

    await onReplanTask(taskId, toDateInputValue(nextDate));
    setSelectedProjectKey(project.projectKey);
    setSelectedPhaseKey("");
  };

  return (
    <section className="space-y-4 rounded-[28px] border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
      <header className="rounded-2xl border border-slate-200 bg-gradient-to-r from-slate-900 via-slate-800 to-slate-700 p-4 text-white">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h3 className="text-lg font-semibold">Projecttijdlijn + targets</h3>
            <p className="text-sm text-slate-200">
              Live projecten en werkbonnen met targetlijn, afwijking in dagen en bewerkbare fase-targets.
            </p>
          </div>
          <div className="rounded-xl bg-white/10 px-3 py-1 text-xs font-medium text-slate-100">
            Bereik: {formatRange(model.minDate, model.maxDate)}
          </div>
        </div>

        <div className="mt-4 grid gap-2 sm:grid-cols-4">
          <div className="rounded-xl bg-white/10 px-3 py-2">
            <p className="text-xs uppercase tracking-wide text-slate-200">Projecten</p>
            <p className="text-xl font-semibold">{model.projects.length}</p>
          </div>
          <div className="rounded-xl bg-white/10 px-3 py-2">
            <p className="text-xs uppercase tracking-wide text-slate-200">Werkbonnen</p>
            <p className="text-xl font-semibold">{model.totalItems}</p>
          </div>
          <div className="rounded-xl bg-white/10 px-3 py-2">
            <p className="text-xs uppercase tracking-wide text-slate-200">Taken</p>
            <p className="text-xl font-semibold">{model.totalTasks}</p>
          </div>
          <div className="rounded-xl bg-white/10 px-3 py-2">
            <p className="text-xs uppercase tracking-wide text-slate-200">Planningvenster</p>
            <p className="text-xl font-semibold">{model.totalDays} dagen</p>
          </div>
        </div>
      </header>

      <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3">
        <div className="mb-2 flex flex-wrap items-center gap-2 text-xs text-slate-600">
          {WORKORDER_PHASE_ORDER.map((phaseKey) => (
            <span key={phaseKey} className="inline-flex items-center gap-1.5 rounded-full bg-white px-2 py-1">
              <span className={`h-2.5 w-2.5 rounded-full ${WORKORDER_PHASE_META[phaseKey].dotClass}`} />
              {WORKORDER_PHASE_META[phaseKey].label}
            </span>
          ))}
          <span className="inline-flex items-center gap-1.5 rounded-full bg-white px-2 py-1">
            <span className="h-2.5 w-2.5 rounded-full border border-dashed border-slate-500" />
            Targetlijn
          </span>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-white px-2 py-1">
            <span className="h-2.5 w-2.5 rounded" style={{ backgroundColor: timelineColors.projectBar }} />
            Projectbalk
          </span>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-white px-2 py-1">
            <span className="h-2.5 w-2.5 rotate-45 bg-rose-500" />
            Taak
          </span>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-white px-2 py-1">
            <span className="h-2.5 w-2.5 rounded" style={{ backgroundColor: timelineColors.currentWeek }} />
            Huidige week
          </span>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-white px-2 py-1">
            <span className="h-2.5 w-2.5 rounded" style={{ backgroundColor: timelineColors.weekend }} />
            Weekend
          </span>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-white px-2 py-1">
            <span className="h-2.5 w-2.5 rounded" style={{ backgroundColor: timelineColors.todayLine }} />
            Vandaag lijn
          </span>
        </div>
        <div className="mb-2 flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => setShowColorSettings((prev) => !prev)}
            className="rounded-lg border border-slate-300 bg-white px-2.5 py-1 text-xs font-medium text-slate-700 hover:bg-slate-100"
          >
            {showColorSettings ? "Kleurinstellingen sluiten" : "Kleurinstellingen openen"}
          </button>
          {showColorSettings ? (
            <button
              type="button"
              onClick={resetTimelineColors}
              className="rounded-lg border border-slate-300 bg-white px-2.5 py-1 text-xs font-medium text-slate-700 hover:bg-slate-100"
            >
              Herstel standaardkleuren
            </button>
          ) : null}
        </div>
        {showColorSettings ? (
          <div className="mb-2 grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
            {[
              { key: "projectBar", label: "Projectbalk" },
              { key: "weekdayBackground", label: "Werkdagen achtergrond" },
              { key: "gridLine", label: "Dag rasterlijn" },
              { key: "weekend", label: "Weekend kleur" },
              { key: "currentWeek", label: "Huidige week" },
              { key: "todayLine", label: "Vandaag lijn" },
            ].map((field) => (
              <label key={field.key} className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs">
                <span className="font-medium text-slate-700">{field.label}</span>
                <input
                  type="color"
                  value={timelineColors[field.key]}
                  onChange={(event) => updateTimelineColor(field.key, event.target.value)}
                  className="h-7 w-10 cursor-pointer rounded border border-slate-300 bg-white"
                />
              </label>
            ))}
          </div>
        ) : null}
        {model.usingFallback ? (
          <p className="text-xs text-slate-500">Er was nog geen bruikbare project- of werkbondata, daarom staat een korte voorbeeldset aan.</p>
        ) : null}
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white">
        <div className="p-3 sm:p-4">
          <div className="space-y-3">
            {model.projects.map((project) => {
              const isSelected = selectedProjectKey === project.projectKey;
              const lifecycleLeft = (diffDays(model.minDate, project.timelineStart) / model.totalDays) * 100;
              const lifecycleWidth = Math.max(4, ((diffDays(project.timelineStart, project.timelineEnd) + 1) / model.totalDays) * 100);

              const targetStart = project.targets.targetStartDate || project.timelineStart;
              const targetEnd = project.targets.targetEndDate || project.timelineEnd;
              const targetLeft = (diffDays(model.minDate, targetStart) / model.totalDays) * 100;
              const targetWidth = Math.max(4, ((diffDays(targetStart, targetEnd) + 1) / model.totalDays) * 100);

              return (
                <div key={project.projectKey} className="grid grid-cols-[320px_minmax(0,1fr)] gap-3">
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={() => {
                      setSelectedProjectKey(project.projectKey);
                      setSelectedPhaseKey("");
                    }}
                    onKeyDown={(event) => {
                      if (event.key !== "Enter" && event.key !== " ") return;
                      event.preventDefault();
                      setSelectedProjectKey(project.projectKey);
                      setSelectedPhaseKey("");
                    }}
                    className={`sticky left-0 z-20 rounded-xl border px-3 py-2 text-left transition ${
                      isSelected
                        ? "border-blue-300 bg-blue-50"
                        : "border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50"
                    }`}
                  >
                    <p className="truncate text-sm font-semibold text-slate-800">{project.projectLabel}</p>
                    <p className="truncate text-xs text-slate-500">{project.clientName}</p>
                    <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px] text-slate-600">
                      <span className={`inline-flex items-center rounded-full px-2 py-0.5 font-semibold ${project.statusMeta.badgeClass}`}>
                        {project.statusMeta.label}
                      </span>
                      <button
                        type="button"
                        onClick={(event) => {
                          event.stopPropagation();
                          setSelectedProjectKey(project.projectKey);
                          const phaseWithItems = project.phases.find((phase) => Number(phase?.count || 0) > 0);
                          if (phaseWithItems) {
                            setSelectedPhaseKey(phaseWithItems.key);
                            return;
                          }
                          setSelectedPhaseKey("");
                          if (project.items[0]) onWorkorderOpen?.(project.items[0]);
                        }}
                        disabled={!project.items.length}
                        className="rounded-full border border-slate-300 bg-white px-2 py-0.5 font-semibold text-slate-700 underline-offset-2 hover:border-slate-400 hover:text-slate-900 hover:underline disabled:cursor-not-allowed disabled:opacity-50 disabled:no-underline"
                        title={project.items.length ? "Toon gekoppelde werkbonnen" : "Geen gekoppelde werkbonnen"}
                      >
                        {project.items.length} werkbonnen
                      </button>
                      <span className="h-1 w-1 rounded-full bg-slate-300" />
                      <span>{project.durationDays} dagen</span>
                    </div>
                  </div>

                  <div className="overflow-x-auto rounded-xl border border-slate-100 bg-slate-50">
                    <div style={{ width: `${model.timelineCanvasWidth}px` }}>
                      <div className="relative h-8 border-b border-slate-200 bg-white/90">
                        {dayTicks.map((tick) => {
                          const left = (diffDays(model.minDate, tick) / model.totalDays) * 100;
                          const day = tick.getDay();
                          const isWeekendDay = day === 0 || day === 6;
                          return (
                            <div
                              key={`row-day-${project.projectKey}-${tick.toISOString()}`}
                              className="absolute inset-y-0 z-0"
                              style={{
                                left: `${left}%`,
                                width: `${dayCellWidthPercent}%`,
                                borderLeft: `1px solid ${gridLineColorSoft}`,
                                backgroundColor: isWeekendDay ? weekendHeaderColorSoft : "transparent",
                              }}
                              aria-hidden="true"
                            >
                              <span className="absolute bottom-0 left-1/2 z-10 -translate-x-1/2 text-[10px] font-semibold text-slate-600">
                                {formatWeekdayInitial(tick)}
                              </span>
                            </div>
                          );
                        })}
                        {model.weekTicks.map((tick) => {
                          const left = (diffDays(model.minDate, tick) / model.totalDays) * 100;
                          return (
                            <div
                              key={`row-week-${project.projectKey}-${tick.toISOString()}`}
                              className="absolute inset-y-0 z-20"
                              style={{ left: `${left}%` }}
                            >
                              <div className="h-full border-l border-slate-300" />
                              <span className="absolute top-0 ml-1 whitespace-nowrap text-[10px] font-medium text-slate-500">
                                {formatWeekTickLabel(tick)}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                      <div
                        className="relative h-[112px]"
                        style={{
                          backgroundColor: timelineColors.weekdayBackground,
                          backgroundImage: `linear-gradient(to right, ${gridLineColorSoft} 1px, transparent 1px)`,
                          backgroundSize: `${100 / model.totalDays}% 100%`,
                        }}
                        onClick={(event) => {
                          const rect = event.currentTarget.getBoundingClientRect();
                          const rawPercent = ((event.clientX - rect.left) / rect.width) * 100;
                          const leftPercent = clamp(rawPercent, 0, 100);
                          const maxOffset = Math.max(0, model.totalDays - 1);
                          const dayOffset = Math.round((leftPercent / 100) * maxOffset);
                          const clickedDate = addDays(model.minDate, dayOffset);

                          setSelectedProjectKey(project.projectKey);
                          setSelectedPhaseKey("");
                          setQuickTaskDraft({
                            projectKey: project.projectKey,
                            plannedDate: toDateInputValue(clickedDate),
                            leftPercent,
                            title: "",
                            prio: "normaal",
                          });
                        }}
                        onDragOver={(event) => {
                          if (!onReplanTask) return;
                          event.preventDefault();
                          event.dataTransfer.dropEffect = "move";
                        }}
                        onDrop={(event) => handleTaskDrop(event, project)}
                      >
                    {weekendBands.map((band) => (
                      <div
                        key={band.key}
                        className="absolute inset-y-0 z-0"
                        style={{ left: `${band.left}%`, width: `${band.width}%`, backgroundColor: weekendColorSoft }}
                        title={band.label}
                      />
                    ))}
                    {weekInRange ? (
                      <div
                        className="absolute inset-y-0 z-[1] rounded"
                        style={{ left: `${weekLeftPercent}%`, width: `${weekWidthPercent}%`, backgroundColor: currentWeekColorSoft }}
                        title="Huidige week"
                      />
                    ) : null}
                    {todayInRange ? (
                      <div
                        className="absolute inset-y-0 z-10 w-[2px]"
                        style={{ left: `${todayLeftPercent}%`, backgroundColor: timelineColors.todayLine }}
                        title="Vandaag"
                      />
                    ) : null}
                    <div
                      className="absolute top-2 h-2 rounded-full border border-dashed border-slate-500 bg-white/70"
                      style={{ left: `${targetLeft}%`, width: `${targetWidth}%` }}
                      title={`Targetperiode: ${formatDateWithWeek(targetStart)} t/m ${formatDateWithWeek(targetEnd)}`}
                    />
                    <div
                      className="absolute top-6 h-2 rounded-full"
                      style={{ left: `${lifecycleLeft}%`, width: `${lifecycleWidth}%`, backgroundColor: timelineColors.projectBar }}
                      title={`Projectstatus: ${project.statusMeta.label}`}
                    />

                    {project.phases.map((phase) => {
                      const left = (diffDays(model.minDate, phase.startDate) / model.totalDays) * 100;
                      const width = Math.max(4, ((diffDays(phase.startDate, phase.endDate) + 1) / model.totalDays) * 100);
                      const phaseSelected = isSelected && selectedPhaseKey === phase.key;
                      const phaseTargetStart = phase.target?.targetStartDate;
                      const phaseTargetEnd = phase.target?.targetEndDate;
                      const phaseTargetLeft = phaseTargetStart ? (diffDays(model.minDate, phaseTargetStart) / model.totalDays) * 100 : null;
                      const phaseTargetWidth = phaseTargetStart && phaseTargetEnd
                        ? Math.max(4, ((diffDays(phaseTargetStart, phaseTargetEnd) + 1) / model.totalDays) * 100)
                        : null;

                      return (
                        <div key={`${project.projectKey}-${phase.key}`}>
                          {phaseTargetLeft !== null && phaseTargetWidth !== null ? (
                            <div
                              className="absolute top-[52px] h-3 rounded border border-dashed border-slate-400 bg-white/60"
                              style={{ left: `${phaseTargetLeft}%`, width: `${phaseTargetWidth}%` }}
                              title={`${phase.label} target: ${formatDateWithWeek(phaseTargetStart)} t/m ${formatDateWithWeek(phaseTargetEnd)}`}
                            />
                          ) : null}
                          <button
                            type="button"
                            onClick={(event) => {
                              event.stopPropagation();
                              setSelectedProjectKey(project.projectKey);
                              setSelectedPhaseKey(phase.key);
                            }}
                            className={`absolute top-9 h-10 rounded-lg px-2 text-left text-[11px] font-semibold text-white shadow-sm transition hover:brightness-110 ${phase.barClass} ${
                              phaseSelected ? "ring-2 ring-white ring-offset-2 ring-offset-slate-200" : ""
                            }`}
                            style={{ left: `${left}%`, width: `${width}%` }}
                            title={`${phase.label} (${phase.count}) • ${formatDateWithWeek(phase.startDate)} t/m ${formatDateWithWeek(phase.endDate)}`}
                          >
                            <span className="block truncate">{phase.label}</span>
                            <span className="block truncate font-medium text-white/90">{phase.count} werkbonnen</span>
                          </button>
                        </div>
                      );
                    })}

                    {project.tasks.map((task) => {
                      const left = (diffDays(model.minDate, task.plannedDate) / model.totalDays) * 100;
                      const tone =
                        task.prio === "hoog"
                          ? "bg-rose-600"
                          : task.prio === "laag"
                          ? "bg-emerald-500"
                          : "bg-slate-700";
                      return (
                        <div
                          key={`${project.projectKey}-${task.id}`}
                          className="group absolute top-0 -translate-x-1/2 -translate-y-1/2"
                          style={{ left: `${left}%` }}
                        >
                          <button
                            type="button"
                            draggable={Boolean(onReplanTask && task.persisted)}
                            onDragStart={(event) => handleTaskDragStart(event, task, project.projectKey)}
                            onMouseDown={(event) => {
                              event.stopPropagation();
                              setSelectedProjectKey(project.projectKey);
                              setSelectedTaskId(String(task.id || ""));
                            }}
                            onClick={(event) => {
                              event.stopPropagation();
                              setSelectedProjectKey(project.projectKey);
                              setSelectedTaskId(String(task.id || ""));
                            }}
                            className={`h-6 w-6 rounded-full bg-white/85 ring-1 ring-slate-300 ${
                              onReplanTask && task.persisted ? "cursor-move" : "cursor-pointer"
                            } ${String(replanningTaskId || "") === String(task.id || "") ? "opacity-60" : ""}`}
                            aria-label={`Taak ${task.title}`}
                            title={`Taak: ${task.title} (${task.prio}) • ${formatDateWithWeek(task.plannedDate)} • klik om te bewerken, sleep om te verplaatsen`}
                          >
                            <span className={`mx-auto block h-3 w-3 rotate-45 rounded-[2px] ${tone} ring-1 ring-white`} />
                          </button>
                          <div className="pointer-events-none absolute left-1/2 top-[-0.55rem] z-40 hidden w-max max-w-[240px] -translate-x-1/2 -translate-y-full rounded-md bg-slate-900 px-2 py-1 text-[11px] font-medium text-white shadow-lg group-hover:block group-focus-within:block">
                            {task.title} • {formatDateWithWeek(task.plannedDate)} • {task.prio}
                          </div>
                        </div>
                      );
                    })}

                      {quickTaskDraft && quickTaskDraft.projectKey === project.projectKey ? (
                      <div
                        className="absolute z-30 w-56 -translate-x-1/2 rounded-xl border border-slate-300 bg-white p-2.5 shadow-lg"
                        style={{ left: `${quickTaskDraft.leftPercent}%`, top: "56px" }}
                        onClick={(event) => event.stopPropagation()}
                      >
                        <p className="mb-1 text-[11px] font-semibold text-slate-700">
                          Nieuwe taak op {formatDateDMY(quickTaskDraft.plannedDate)}
                          {quickTaskDraft.plannedDate ? ` (${formatWeekdayNameForInput(quickTaskDraft.plannedDate)})` : ""}
                        </p>
                        <input
                          type="text"
                          value={quickTaskDraft.title}
                          onChange={(event) =>
                            setQuickTaskDraft((prev) =>
                              prev
                                ? { ...prev, title: event.target.value }
                                : prev
                            )
                          }
                          className="mb-1.5 w-full rounded border border-slate-300 px-2 py-1 text-xs"
                          placeholder="Taaktitel"
                          autoFocus
                        />
                        <select
                          value={quickTaskDraft.prio}
                          onChange={(event) =>
                            setQuickTaskDraft((prev) =>
                              prev
                                ? { ...prev, prio: event.target.value }
                                : prev
                            )
                          }
                          className="mb-1.5 w-full rounded border border-slate-300 px-2 py-1 text-xs"
                        >
                          <option value="laag">Laag</option>
                          <option value="normaal">Normaal</option>
                          <option value="hoog">Hoog</option>
                        </select>
                        <div className="flex gap-1.5">
                          <button
                            type="button"
                            onClick={() => setQuickTaskDraft(null)}
                            className="flex-1 rounded bg-slate-100 px-2 py-1 text-[11px] font-semibold text-slate-700 hover:bg-slate-200"
                          >
                            Annuleer
                          </button>
                          <button
                            type="button"
                            onClick={() => submitQuickTask(project)}
                            disabled={isCreatingTaskCurrent}
                            className="flex-1 rounded bg-rose-600 px-2 py-1 text-[11px] font-semibold text-white hover:bg-rose-500 disabled:opacity-60"
                          >
                            Voeg toe
                          </button>
                        </div>
                      </div>
                      ) : null}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {selectedProject ? (
        <section className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_380px]">
          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
              <div>
                <p className="text-base font-semibold text-slate-900">{selectedProject.projectLabel}</p>
                <p className="text-xs text-slate-500">{selectedProject.clientName}</p>
              </div>
              <button
                type="button"
                onClick={() => onProjectOpen?.(selectedProject)}
                className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-100"
              >
                Open projectdetail
              </button>
            </div>

            <div className="mb-3 flex flex-wrap gap-2 text-xs">
              {selectedProject.targets.targetEndDate ? (
                <span className={`inline-flex rounded-full px-2 py-1 font-semibold ${
                  targetDeltaDays === null
                    ? "bg-slate-100 text-slate-700"
                    : targetDeltaDays > 0
                    ? "bg-rose-100 text-rose-700"
                    : "bg-emerald-100 text-emerald-700"
                }`}>
                  {targetDeltaDays > 0 ? `+${targetDeltaDays} dagen uitloop` : `${Math.abs(targetDeltaDays || 0)} dagen speling`}
                </span>
              ) : (
                <span className="inline-flex rounded-full bg-slate-100 px-2 py-1 font-semibold text-slate-700">Geen eindtarget gezet</span>
              )}
              <span className="inline-flex rounded-full bg-slate-100 px-2 py-1 font-semibold text-slate-700">
                Doel uren: {Number(selectedProject.targets.targetHours || 0)}
              </span>
              <span className="inline-flex rounded-full bg-slate-100 px-2 py-1 font-semibold text-slate-700">
                Doel omzet: {formatMoney(selectedProject.targets.targetRevenueExcl || 0)}
              </span>
              <span className="inline-flex rounded-full bg-slate-100 px-2 py-1 font-semibold text-slate-700">
                Taken: {selectedProject.tasks.length}
              </span>
            </div>

            {selectedProject.tasks.length ? (
              <div className="mb-3 rounded-xl border border-slate-200 bg-white p-2.5">
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Tijdlijntaken</p>
                <div className="grid gap-1.5 sm:grid-cols-2">
                  {selectedProject.tasks.slice(0, 8).map((task) => (
                    <button
                      key={task.id}
                      type="button"
                      onClick={() => setSelectedTaskId(String(task.id || ""))}
                      className={`rounded-lg border px-2 py-1.5 text-left ${
                        String(selectedTaskId || "") === String(task.id || "")
                          ? "border-blue-300 bg-blue-50"
                          : "border-slate-200 bg-white hover:border-slate-300"
                      }`}
                    >
                      <p className="truncate text-xs font-semibold text-slate-800">{task.title}</p>
                      <p className="text-[11px] text-slate-500">
                        {formatDateDMY(task.plannedDate)} • {task.prio}
                      </p>
                    </button>
                  ))}
                </div>
              </div>
            ) : null}

            {selectedTask ? (
              <form
                onSubmit={async (event) => {
                  event.preventDefault();
                  if (!selectedTask.persisted || !onUpdateTask) return;
                  await onUpdateTask(selectedTask.id, {
                    title: String(taskEditDraft.title || "").trim(),
                    plannedDate: taskEditDraft.plannedDate || null,
                    prio: taskEditDraft.prio || "normaal",
                    status: taskEditDraft.status || "open",
                  });
                }}
                className="mb-3 rounded-xl border border-slate-200 bg-white p-2.5"
              >
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Taak bewerken</p>
                {!selectedTask.persisted ? (
                  <p className="mb-2 text-xs text-slate-500">Demo-taak: bewerken/verwijderen staat alleen aan voor echte taken.</p>
                ) : null}
                <div className="grid gap-2 sm:grid-cols-2">
                  <input
                    type="text"
                    value={taskEditDraft.title}
                    onChange={(e) => setTaskEditDraft((prev) => ({ ...prev, title: e.target.value }))}
                    className="rounded-lg border border-slate-300 px-2 py-1.5 text-sm sm:col-span-2"
                    placeholder="Taaktitel"
                    disabled={!selectedTask.persisted || String(updatingTaskId || "") === String(selectedTask.id || "")}
                    required
                  />
                  <input
                    type="date"
                    value={taskEditDraft.plannedDate}
                    onChange={(e) => setTaskEditDraft((prev) => ({ ...prev, plannedDate: e.target.value }))}
                    className="rounded-lg border border-slate-300 px-2 py-1.5 text-sm"
                    disabled={!selectedTask.persisted || String(updatingTaskId || "") === String(selectedTask.id || "")}
                  />
                  <select
                    value={taskEditDraft.prio}
                    onChange={(e) => setTaskEditDraft((prev) => ({ ...prev, prio: e.target.value }))}
                    className="rounded-lg border border-slate-300 px-2 py-1.5 text-sm"
                    disabled={!selectedTask.persisted || String(updatingTaskId || "") === String(selectedTask.id || "")}
                  >
                    <option value="laag">Laag</option>
                    <option value="normaal">Normaal</option>
                    <option value="hoog">Hoog</option>
                  </select>
                  <select
                    value={taskEditDraft.status}
                    onChange={(e) => setTaskEditDraft((prev) => ({ ...prev, status: e.target.value }))}
                    className="rounded-lg border border-slate-300 px-2 py-1.5 text-sm sm:col-span-2"
                    disabled={!selectedTask.persisted || String(updatingTaskId || "") === String(selectedTask.id || "")}
                  >
                    <option value="open">Open</option>
                    <option value="in_uitvoering">In uitvoering</option>
                    <option value="wachten">Wachten</option>
                    <option value="gereed">Gereed</option>
                    <option value="niet_gereed">Niet gereed</option>
                    <option value="geannuleerd">Geannuleerd</option>
                  </select>
                </div>
                <div className="mt-2 flex gap-2">
                  <button
                    type="submit"
                    disabled={!selectedTask.persisted || String(updatingTaskId || "") === String(selectedTask.id || "")}
                    className="flex-1 rounded-lg bg-blue-600 px-3 py-2 text-sm font-semibold text-white hover:bg-blue-500 disabled:opacity-60"
                  >
                    {String(updatingTaskId || "") === String(selectedTask.id || "") ? "Opslaan..." : "Opslaan"}
                  </button>
                  <button
                    type="button"
                    onClick={async () => {
                      if (!selectedTask.persisted || !onDeleteTask) return;
                      const ok = window.confirm("Taak verwijderen?");
                      if (!ok) return;
                      const removed = await onDeleteTask(selectedTask.id);
                      if (removed) setSelectedTaskId("");
                    }}
                    disabled={!selectedTask.persisted || String(deletingTaskId || "") === String(selectedTask.id || "")}
                    className="rounded-lg border border-rose-300 bg-rose-50 px-3 py-2 text-sm font-semibold text-rose-700 hover:bg-rose-100 disabled:opacity-60"
                  >
                    {String(deletingTaskId || "") === String(selectedTask.id || "") ? "Verwijderen..." : "Verwijder"}
                  </button>
                </div>
              </form>
            ) : null}

            {selectedPhase ? (
              <>
                <div className="mb-3 flex items-center gap-2">
                  <span className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-semibold ${selectedPhase.badgeClass}`}>
                    {selectedPhase.label}
                  </span>
                  <span className="text-xs text-slate-500">{selectedPhase.count} gekoppelde werkbonnen</span>
                </div>

                <div className="grid gap-2 sm:grid-cols-2">
                  {selectedPhase.items.map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => onWorkorderOpen?.(item)}
                      className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-left transition hover:border-slate-300 hover:bg-slate-50"
                    >
                      <p className="truncate text-sm font-semibold text-slate-800">{item.werkbonNummer} - {item.title}</p>
                      <p className="truncate text-xs text-slate-500">{item.clientName}</p>
                      <p className="truncate text-xs text-slate-500">{item.locationLabel}</p>
                    </button>
                  ))}
                </div>
              </>
            ) : (
              <p className="text-sm text-slate-600">Klik op een faseblok om gekoppelde werkbonnen te tonen.</p>
            )}
          </div>

          <div className="space-y-4">
            <form onSubmit={submitTask} className="rounded-2xl border border-slate-200 bg-white p-4">
              <p className="text-sm font-semibold text-slate-900">+ Taak op tijdlijn</p>
              <p className="mb-3 text-xs text-slate-500">Plaats een eigen taak direct op de projecttijdlijn.</p>
              <div className="grid gap-2">
                <input
                  type="text"
                  value={taskDraft.title}
                  onChange={(e) => setTaskDraft((prev) => ({ ...prev, title: e.target.value }))}
                  className="rounded-lg border border-slate-300 px-2 py-1.5 text-sm"
                  placeholder="Taaktitel"
                  required
                />
                <div className="grid gap-2 sm:grid-cols-2">
                  <div>
                    <input
                      type="date"
                      value={taskDraft.plannedDate}
                      onChange={(e) => setTaskDraft((prev) => ({ ...prev, plannedDate: e.target.value }))}
                      className="w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm"
                    />
                    {taskDraft.plannedDate ? (
                      <p className="mt-1 text-[11px] text-slate-500">
                        Datum: {formatDateDMY(taskDraft.plannedDate)} • Dag: {formatWeekdayNameForInput(taskDraft.plannedDate)}
                      </p>
                    ) : null}
                  </div>
                  <select
                    value={taskDraft.prio}
                    onChange={(e) => setTaskDraft((prev) => ({ ...prev, prio: e.target.value }))}
                    className="rounded-lg border border-slate-300 px-2 py-1.5 text-sm"
                  >
                    <option value="laag">Laag</option>
                    <option value="normaal">Normaal</option>
                    <option value="hoog">Hoog</option>
                  </select>
                </div>
              </div>
              <button
                type="submit"
                disabled={isCreatingTaskCurrent}
                className="mt-3 w-full rounded-lg bg-rose-600 px-3 py-2 text-sm font-semibold text-white hover:bg-rose-500 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {isCreatingTaskCurrent ? "Taak toevoegen..." : "Taak toevoegen"}
              </button>
            </form>

            <form onSubmit={submitTargets} className="rounded-2xl border border-slate-200 bg-white p-4">
              <p className="text-sm font-semibold text-slate-900">Targets</p>
              <p className="mb-3 text-xs text-slate-500">Project- en fase-targets voor planning en voortgang.</p>

              <div className="grid gap-2 sm:grid-cols-2">
                <label className="text-xs text-slate-600">
                  Starttarget
                  <input
                    type="date"
                    value={targetDraft.targetStartDate}
                    onChange={(e) => setTargetDraft((prev) => ({ ...prev, targetStartDate: e.target.value }))}
                    className="mt-1 w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm"
                  />
                </label>
                <label className="text-xs text-slate-600">
                  Eindtarget
                  <input
                    type="date"
                    value={targetDraft.targetEndDate}
                    onChange={(e) => setTargetDraft((prev) => ({ ...prev, targetEndDate: e.target.value }))}
                    className="mt-1 w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm"
                  />
                </label>
                <label className="text-xs text-slate-600">
                  Doel uren
                  <input
                    type="number"
                    min="0"
                    step="1"
                    value={targetDraft.targetHours}
                    onChange={(e) => setTargetDraft((prev) => ({ ...prev, targetHours: e.target.value }))}
                    className="mt-1 w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm"
                  />
                </label>
                <label className="text-xs text-slate-600">
                  Doel omzet excl.
                  <input
                    type="number"
                    min="0"
                    step="1"
                    value={targetDraft.targetRevenueExcl}
                    onChange={(e) => setTargetDraft((prev) => ({ ...prev, targetRevenueExcl: e.target.value }))}
                    className="mt-1 w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm"
                  />
                </label>
                <label className="text-xs text-slate-600">
                  Doel ingepland (uren)
                  <input
                    type="number"
                    min="0"
                    step="1"
                    value={targetDraft.targetPlannedHours}
                    onChange={(e) => setTargetDraft((prev) => ({ ...prev, targetPlannedHours: e.target.value }))}
                    className="mt-1 w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm"
                  />
                </label>
                <label className="text-xs text-slate-600">
                  Doel afronding %
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="1"
                    value={targetDraft.targetCompletionRate}
                    onChange={(e) => setTargetDraft((prev) => ({ ...prev, targetCompletionRate: e.target.value }))}
                    className="mt-1 w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm"
                  />
                </label>
              </div>

              <div className="mt-3 space-y-2 rounded-xl border border-slate-200 bg-slate-50 p-2.5">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Fase-targets</p>
                {targetDraft.phaseTargets.map((phaseTarget, index) => (
                  <div key={phaseTarget.key} className="grid gap-1.5 rounded-lg border border-slate-200 bg-white p-2">
                    <p className="text-xs font-semibold text-slate-700">{phaseTarget.label}</p>
                    <div className="grid gap-1.5 sm:grid-cols-3">
                      <input
                        type="date"
                        value={phaseTarget.targetStartDate}
                        onChange={(e) =>
                          setTargetDraft((prev) => {
                            const next = [...prev.phaseTargets];
                            next[index] = { ...next[index], targetStartDate: e.target.value };
                            return { ...prev, phaseTargets: next };
                          })
                        }
                        className="rounded-lg border border-slate-300 px-2 py-1.5 text-xs"
                      />
                      <input
                        type="date"
                        value={phaseTarget.targetEndDate}
                        onChange={(e) =>
                          setTargetDraft((prev) => {
                            const next = [...prev.phaseTargets];
                            next[index] = { ...next[index], targetEndDate: e.target.value };
                            return { ...prev, phaseTargets: next };
                          })
                        }
                        className="rounded-lg border border-slate-300 px-2 py-1.5 text-xs"
                      />
                      <input
                        type="number"
                        min="0"
                        step="1"
                        value={phaseTarget.targetCount}
                        onChange={(e) =>
                          setTargetDraft((prev) => {
                            const next = [...prev.phaseTargets];
                            next[index] = { ...next[index], targetCount: e.target.value };
                            return { ...prev, phaseTargets: next };
                          })
                        }
                        className="rounded-lg border border-slate-300 px-2 py-1.5 text-xs"
                        placeholder="Aantal"
                      />
                    </div>
                  </div>
                ))}
              </div>

              <button
                type="submit"
                disabled={isSavingCurrent}
                className="mt-3 w-full rounded-lg bg-slate-900 px-3 py-2 text-sm font-semibold text-white hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {isSavingCurrent ? "Targets opslaan..." : "Targets opslaan"}
              </button>
            </form>
          </div>
        </section>
      ) : (
        <section className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-4 text-sm text-slate-600">
          Kies eerst een project in de linkerkolom. Daarna kun je targets invullen en per fase bijsturen.
        </section>
      )}
    </section>
  );
}
